import { useMobileScrollReveal } from "@/hooks/useMobileScrollReveal";
import { ConnectedUnifiedHomepage } from "@/components/merchandising/ConnectedUnifiedHomepage";
import { LightQuiz, WhyTruWeSection, CompareTable, ScrollHighlightStory, SourcingPledge } from "@/components/merchandising/TruWeMerchandising";
import { CmsRegion } from "@/components/cms/CmsRegion";
import { merchantOfferExtras } from "@/lib/seo/offer-schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  FlaskConical,
  Leaf,
  ShieldCheck,
  BadgeCheck,
  Microscope,
  Eye,
} from "lucide-react";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { getRecentReviewsMix, getSiteReviewStats } from "@/lib/reviews.functions";
import { getCatalogPricing } from "@/lib/pricing/catalog-pricing.functions";

import { Button } from "@/components/ui/button";

import { BundlesSection } from "@/components/BundlesSection";
import { VerifiedPurchaseBadge } from "@/components/reviews/VerifiedPurchaseBadge";
import { BetaTesterBadge } from "@/components/reviews/BetaTesterBadge";
import { StarRating } from "@/components/reviews/StarRating";
import { VerticalReviewSlider } from "@/components/reviews/VerticalReviewSlider";
import { ResearchTriggerBand } from "@/components/ResearchDrawer";
import { Reveal, KineticHeadline } from "@/components/motion/Reveal";
import { FloatingProduct } from "@/components/motion/FloatingProduct";
import heroMag__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const heroMag = heroMag__asset.url;
import heroOva__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const heroOva = heroOva__asset.url;
import heroLiver__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const heroLiver = heroLiver__asset.url;

import { useCountdown, formatINR } from "@/hooks/useCountdown";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { buildFaqPageSchema } from "@/lib/faqSchema";
import { HeroSlideshow as HeroSlideshowTop, heroPreloadFor } from "@/components/HeroSlideshow";
import { fetchHeroCarousel } from "@/lib/hero-carousel-query";

import { TrustBar } from "@/components/TrustBar";
import { WelcomeBackBanner } from "@/components/identity/WelcomeBackBanner";
import { UrgencyInline } from "@/components/urgency/UrgencyInline";
import { QuickBuySheet } from "@/components/QuickBuySheet";
import { SOLD_OUT, PREORDER_ACTIVE, PREORDER_NOTE_SHORT, isComingSoon } from "@/lib/storeStatus";
import { useState } from "react";
import { DEFAULT_BUNDLE_RULES } from "@/lib/bundles";

const HOME_BUNDLE_SLUGS = ["balance-calm-duo", "balance-detox-duo", "full-ova-balance-protocol"];
const HOME_BUNDLES = DEFAULT_BUNDLE_RULES.filter((r) => HOME_BUNDLE_SLUGS.includes(r.slug));
import { useT } from "@/lib/i18n/provider";



/**
 * Single-pack prices are never hardcoded here: they come from the live
 * catalogue published in Admin → Products (route loader → `livePricing`).
 */
type LivePack = { price: number; mrp: number | null; perDay: number };

function singlePackFrom(
  pricing: Record<string, Partial<Record<1 | 2 | 3, { price: number; mrp: number | null }>>> | undefined,
  handle: string,
): LivePack | null {
  const pack = pricing?.[handle]?.[1];
  if (!pack) return null;
  return { price: pack.price, mrp: pack.mrp, perDay: Math.round(pack.price / 30) };
}

function offerPrice(
  pricing: Record<string, Partial<Record<1 | 2 | 3, { price: number; mrp: number | null }>>> | undefined,
  handle: string,
): string | null {
  const pack = pricing?.[handle]?.[1];
  return pack ? pack.price.toFixed(2) : null;
}

function SkuPrice({ price, perDay }: { price: number; compare?: number | null; perDay?: number | null }) {
  const t = useT();
  const { ref, seen } = useInViewOnce<HTMLDivElement>();
  const animatedPrice = useCountdown(seen ? price : 0, price, 1500, seen ? "in" : "out");
  return (
    <div ref={ref} data-testid="price-countdown">
      <p
        className="font-display text-2xl font-semibold sku-text tabular-nums"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        ₹{formatINR(animatedPrice)}
      </p>
      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {t("home.sku.perPack")}
      </p>
      {perDay ? (
        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>
          {t("home.sku.perDay", { amount: perDay })}
        </p>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute("/")({
  /** `?classic=1` falls back to the previous homepage hero. */
  validateSearch: (search: Record<string, unknown>): { classic?: 1 } =>
    String(search["classic"] ?? "") === "1" ? { classic: 1 } : {},
  beforeLoad: () => {
    if (typeof window !== "undefined" && window.location.hostname === "admin.truwe.in") {
      window.location.replace("/admin");
    }
  },
  loader: async () => {
    // Site-wide rating is rendered server-side so search engines and AI
    // crawlers read the same aggregate shoppers see in the reviews section.
    // Prices come from the live catalogue so the page, the structured data
    // and checkout always agree with what Admin published.
    // The hero banner is fetched here too so the first image is in the
    // initial HTML (fast largest paint, no layout jump).
    const [stats, livePricing, heroCarousel] = await Promise.all([
      getSiteReviewStats().catch(() => null),
      getCatalogPricing().catch(() => ({})),
      fetchHeroCarousel().catch(() => null),
    ]);
    return {
      siteReviewStats: stats && stats.count > 0 ? { avg: stats.avg, count: stats.count } : null,
      livePricing,
      heroCarousel,
    };
  },

  component: HomePage,
  head: ({ loaderData }) => ({

    meta: [
      { title: "Buy the Best Health & Wellness Supplements Online in India" },
      {
        name: "description",
        content:
          "Shop Truwe's science-backed health & wellness supplements formulated for high bioavailability and better absorption. Explore solutions for sleep, liver health, gut health, and women's wellness.",
      },
      { name: "keywords", content: "magnesium supplement India, myo inositol PCOS, liver detox supplement, NAC supplement India, Bio-Absorb Matrix, kids gummies no sugar, clinical wellness supplements" },
      { property: "og:title", content: "Buy the Best Health & Wellness Supplements Online in India" },
      {
        property: "og:description",
        content:
          "Shop Truwe's science-backed health & wellness supplements formulated for high bioavailability and better absorption. Explore solutions for sleep, liver health, gut health, and women's wellness.",
      },
      { property: "og:url", content: "https://truwe.in/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://truwe.in/" },
      // Preload the actual first banner the page will show, at the exact
      // variant the browser would pick, so it starts downloading immediately.
      ...(() => {
        const p = heroPreloadFor(loaderData?.heroCarousel?.slides);
        return p
          ? [
              {
                rel: "preload",
                as: "image",
                href: p.href,
                ...(p.imagesrcset ? { imagesrcset: p.imagesrcset, imagesizes: p.imagesizes } : {}),
                fetchpriority: "high",
              } as unknown as { rel: string; href: string },
            ]
          : [];
      })(),
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "TruWe adult formulary",
          numberOfItems: 3 + HOME_BUNDLES.length,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              url: "https://truwe.in/products/mag-absorb-pro",
              item: {
                "@type": "Product",
                name: "Mag Absorb Pro",
                url: "https://truwe.in/products/mag-absorb-pro",
                image: "https://truwe.in/og-image.jpg",
                description:
                  "TruWe Mag Absorb Pro: 5 magnesium forms with Bio-Absorb Matrix for sleep, muscle recovery and stress support. 30-day supply.",
                category: "Health & Beauty > Nutritional Supplements",
                brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                offers: {
                  "@type": "Offer",
                  url: "https://truwe.in/products/mag-absorb-pro",
                  ...(offerPrice(loaderData?.livePricing, "mag-absorb-pro")
                    ? { price: offerPrice(loaderData?.livePricing, "mag-absorb-pro") }
                    : {}),
                  priceCurrency: "INR",
                  availability: "https://schema.org/InStock",
                  ...merchantOfferExtras(),
                },
              },
            },
            {
              "@type": "ListItem",
              position: 2,
              url: "https://truwe.in/products/liver-biome-plus",
              item: {
                "@type": "Product",
                name: "Liver Biome Plus",
                url: "https://truwe.in/products/liver-biome-plus",
                image: "https://truwe.in/og-image.jpg",
                description:
                  "TruWe Liver Biome Plus: liposomal silymarin, NAC and 10B CFU probiotics for liver and gut support. 30-day supply.",
                category: "Health & Beauty > Nutritional Supplements",
                brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                offers: {
                  "@type": "Offer",
                  url: "https://truwe.in/products/liver-biome-plus",
                  ...(offerPrice(loaderData?.livePricing, "liver-biome-plus")
                    ? { price: offerPrice(loaderData?.livePricing, "liver-biome-plus") }
                    : {}),
                  priceCurrency: "INR",
                  availability: "https://schema.org/InStock",
                  ...merchantOfferExtras(),
                },
              },
            },
            {
              "@type": "ListItem",
              position: 3,
              url: "https://truwe.in/products/ova-balance",
              item: {
                "@type": "Product",
                name: "Ova Balance",
                url: "https://truwe.in/products/ova-balance",
                image: "https://truwe.in/og-image.jpg",
                description:
                  "TruWe Ova Balance: 40:1 Myo/D-Chiro Inositol with Berberine HCl for PCOS, hormonal balance and cycle regularity. 30-day supply.",
                category: "Health & Beauty > Nutritional Supplements",
                brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                offers: {
                  "@type": "Offer",
                  url: "https://truwe.in/products/ova-balance",
                  ...(offerPrice(loaderData?.livePricing, "ova-balance")
                    ? { price: offerPrice(loaderData?.livePricing, "ova-balance") }
                    : {}),
                  priceCurrency: "INR",
                  availability: "https://schema.org/PreOrder",
                  ...merchantOfferExtras(),
                },
              },
            },
            ...HOME_BUNDLES.map((b, i) => ({
              "@type": "ListItem",
              position: 4 + i,
              url: `https://truwe.in/products/${b.slug}`,
              item: {
                "@type": "Product",
                name: b.title,
                url: `https://truwe.in/products/${b.slug}`,
                image: "https://truwe.in/og-image.jpg",
                description: b.tagline,
                category: "Health & Beauty > Nutritional Supplements",
                brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                isRelatedTo: b.matchHandles.map((h) => ({ "@type": "Product", url: `https://truwe.in/products/${h}` })),
                offers: {
                  "@type": "Offer",
                  url: `https://truwe.in/products/${b.slug}`,
                  priceCurrency: "INR",
                  availability: b.matchHandles.some((h) => isComingSoon(h))
                    ? "https://schema.org/PreOrder"
                    : "https://schema.org/InStock",
                },
              },
            })),
          ],
        }),
      },
      ...(loaderData?.siteReviewStats
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Brand",
                "@id": "https://truwe.in/#organization",
                name: "TruWe",
                url: "https://truwe.in/",
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: loaderData.siteReviewStats.avg,
                  reviewCount: loaderData.siteReviewStats.count,
                  bestRating: 5,
                  worstRating: 1,
                },
              }),
            },
          ]
        : []),
      {

        type: "application/ld+json",
        children: JSON.stringify(
          buildFaqPageSchema({
            pageUrl: "https://truwe.in/",
            faqs: [
              {
                q: "Are TruWe supplements research-backed?",
                a: "Yes. Every active in a TruWe formula is dosed to match published clinical research. Aquamin® marine magnesium and liposomal bisglycinate (Mag Absorb Pro), 40:1 myo + D-chiro inositol (Prof. Vittorio Unfer) and Berberine HCl (Ova Balance), NAC, liposomal silymarin, liposomal curcumin and liposomal ALA (Liver Biome Plus) at evidence-based doses. Full citations are listed on the Ingredients & Research page.",
              },
              {
                q: "Are TruWe products FSSAI licensed and lab tested?",
                a: "Yes. All TruWe formulas are FSSAI licensed and manufactured in GMP-certified, USFDA-registered facilities in India, with third-party lab testing for purity, potency and contaminants.",
              },
              {
                q: "Is Ova Balance a capsule or a sachet?",
                a: "Ova Balance is a daily sachet, one sachet per day, 30-day supply. It uses the gold-standard 40:1 myo-inositol to D-chiro inositol ratio.",
              },
              {
                q: "Do TruWe kids' gummies contain added sugar?",
                a: "No. The TruWe Kids range (Brain+ and Eye+) is formulated with no added sugar, no artificial colours and research-backed actives appropriate for growing bodies.",
              },
              {
                q: "How long does delivery take in India?",
                a: "Orders ship from India within 24 business hours. Free shipping is included on every order, with prepaid orders receiving an additional 5% benefit.",
              },
            ],
          }),
        ),
      },
    ],
  }),
});

function HomePage() {
  const { classic } = Route.useSearch();
  const { heroCarousel } = Route.useLoaderData();
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(12),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const adultProducts = products
    .map((p) => p.node)
    .filter((p) => !p.tags.some((t) => t.toLowerCase() === "truwe-kids"));

  return (
    <div>
      <WelcomeBackBanner />
      {classic === 1 ? (
        <>
          <HeroSlideshowTop initial={heroCarousel ?? undefined} />
          <TrustBar />
        </>
      ) : (
        <ConnectedUnifiedHomepage belowHero={<TrustBar />} heroInitial={heroCarousel ?? undefined} />
      )}

      {/* Optional admin-run sale ribbon ("home" placement) */}
      <div className="mx-auto max-w-7xl px-4 pt-4 md:px-8">
        <UrgencyInline placement="home" />
      </div>
      <CmsRegion id="home-below-hero" label="Home — below the hero" />
      {classic === 1 && <div id="protocols" className="scroll-mt-24" />}

      {/* 1. Three product cards */}
      {classic === 1 && <SkuShowcase products={products} />}

      {/* 2. Bundles & protocols right after product cards */}
      {classic === 1 && <BundlesSection products={adultProducts} />}

      {/* 3. Trust & testing proof */}
      <CertificationStrip />

      {/* 4. Short Why-TruWe comparison */}
      {classic === 1 ? <WhyTruWe /> : <div className="twm twm-shell"><WhyTruWeSection /></div>}

      {/* 4b. Us-vs-Others comparison + scroll-highlight founder note (in TruWe language) */}
      <div className="twm twm-shell">
        <CompareTable />
        <SourcingPledge />
        <ScrollHighlightStory />
      </div>

      {/* 5. Reviews / results (compact) */}
      <div className="twm-home-reviews"><HomeTestimonials /></div>

      {/* 6. Quiz CTA, primary recommendation surface */}
      {classic === 1 ? <QuizTeaser /> : <div className="twm twm-shell"><LightQuiz /></div>}

      <CmsRegion id="home-bottom" label="Home — above the footer" />

    </div>
  );
}

function HomeTestimonials() {
  const headingReveal = useMobileScrollReveal<HTMLHeadingElement>();
  const t = useT();
  const { data, isLoading } = useQuery({
    queryKey: ["home-reviews-mix"],
    // Server functions must be imported statically: a dynamic import keeps the
    // function out of the server-fn manifest, so the RPC 500s with
    // "Invalid server function ID".
    queryFn: () => getRecentReviewsMix({ data: { limit: 6 } }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: stats } = useQuery({
    queryKey: ["home-review-stats"],
    queryFn: () => getSiteReviewStats(),
    staleTime: 5 * 60 * 1000,
  });

  const reviews = data?.reviews ?? [];

  // Never fabricate testimonials. If there are no approved reviews yet, show a
  // neutral "coming soon" state so the section is honest.
  if (isLoading) return null;

  return (
    <section className="section-y mx-auto max-w-7xl px-4 md:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <h2 ref={headingReveal} className="font-display text-3xl font-bold text-brand-deep md:text-4xl">
          {t("home.reviews.title")}
        </h2>
        {stats && stats.count > 0 && (
          <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full border border-border bg-card px-5 py-3">
            <StarRating value={stats.avg} size={18} />
            <span className="font-display text-lg font-bold text-brand-deep">{stats.avg.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">
              out of 5 · {stats.count} {stats.count === 1 ? "review" : "reviews"}
            </span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-dashed border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Verified customer reviews coming soon. We only publish reviews from real, verified buyers. No
            paid or fabricated testimonials.
          </p>
        </div>
      ) : (
        <VerticalReviewSlider
          className="mx-auto mt-8 max-w-2xl"
          height={172}
          slides={reviews.map((r: any) => ({ key: r.id, node: <CompactReviewCard r={r} /> }))}
        />
      )}
    </section>
  );
}

function CompactReviewCard({ r }: { r: any }) {
  const [expanded, setExpanded] = useState(false);
  const comment = r.comment ?? "";
  const isLong = comment.length > 120;
  const shown = expanded || !isLong ? comment : `${comment.slice(0, 120)}…`;

  return (
    <figure className="flex flex-col rounded-2xl border border-border bg-card p-4">
      <div className="mb-1.5 flex items-center gap-1 text-[13px] text-amber-400" aria-label={`${r.rating} out of 5 stars`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className={i < r.rating ? "" : "text-[#D9D9D6]"}>★</span>
        ))}
      </div>
      <blockquote className="text-[13.5px] leading-snug text-brand-deep">"{shown}"</blockquote>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 self-start text-[12px] font-semibold text-brand hover:underline"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
      <figcaption className="mt-2.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        <span>{r.reviewer_name || "Verified buyer"}</span>
        {r.products?.handle ? (
          <>
            <span>·</span>
            <Link to="/products/$handle" params={{ handle: r.products.handle }} className="text-brand hover:underline">
              {r.products.title}
            </Link>
          </>
        ) : null}
        {r.reviewer_kind === "beta_tester" ? (
          <BetaTesterBadge />
        ) : (
          r.is_verified && <VerifiedPurchaseBadge orderRef={r.order_ref} />
        )}
      </figcaption>
    </figure>
  );
}





function SkuShowcase({ products }: { products: ShopifyProduct[] }) {
  const { livePricing } = Route.useLoaderData();
  const t = useT();
  const [quickBuyHandle, setQuickBuyHandle] = useState<string | null>(null);
  const quickBuyProduct = quickBuyHandle
    ? products.find((product) => product.node.handle === quickBuyHandle)?.node ?? null
    : null;
  const skus = [
    {
      sku: "liver" as const,
      src: heroLiver,
      alt: "Liver Biome Plus",
      eyebrow: t("home.eyebrow.liver"),
      title: "Liver Biome Plus",
      tagline: t("home.tagline.liver"),
      copy: t("home.copy.liver"),
      handle: "liver-biome-plus",
    },
    {
      sku: "mag" as const,
      src: heroMag,
      alt: "Mag Absorb Pro",
      eyebrow: t("home.eyebrow.mag"),
      title: "Mag Absorb Pro",
      tagline: t("home.tagline.mag"),
      copy: t("home.copy.mag"),
      handle: "mag-absorb-pro",
    },
    {
      sku: "ova" as const,
      src: heroOva,
      alt: "Ova Balance",
      eyebrow: t("home.eyebrow.ova"),
      title: "Ova Balance",
      tagline: t("home.tagline.ova"),
      copy: t("home.copy.ova"),
      handle: "ova-balance",
    },
  ];

  return (
    <section className="section-y relative mx-auto max-w-7xl px-4 md:px-8">
      {/* Header */}
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brand">
            {t("home.formulary.eyebrow")}
          </p>
        </Reveal>
        <h2 className="mt-4 font-display text-3xl font-extrabold leading-[1.05] text-brand-deep md:text-5xl lg:text-6xl">
          <KineticHeadline text={t("home.formulary.title1")} />{" "}
          <KineticHeadline text={t("home.formulary.title2")} className="text-gradient-brand" />
        </h2>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {t("home.formulary.body")}
          </p>
        </Reveal>
      </div>

      {/* Credibility band */}
      <Reveal delay={0.3}>
        <div className="mt-10">
          <ResearchTriggerBand />
        </div>
      </Reveal>

      {/* Cards */}
      <div className="mt-10 grid gap-4 sm:gap-6 md:mt-20 md:grid-cols-3 md:gap-7">
        {skus.map((s, i) => {
          const product =
            products.find((p) => p.node.handle === s.handle)?.node ??
            products.find((p) => p.node.handle.includes(s.handle.split("-")[0]))?.node;
          const singleUnit = singlePackFrom(livePricing, s.handle);
          const price = singleUnit
            ? singleUnit.price
            : product
              ? parseFloat(product.priceRange.minVariantPrice.amount)
              : null;
          const compare = singleUnit
            ? singleUnit.mrp
            : product?.compareAtPriceRange?.minVariantPrice?.amount
              ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
              : null;
          const perDay = singleUnit ? singleUnit.perDay : null;
          return (
            <Reveal key={s.title} delay={i * 0.1}>
              <FloatingProduct
                src={s.src}
                alt={s.alt}
                sku={s.sku}
                badge={s.eyebrow}
                imageHandle={s.handle}
              >
                <div className="mt-4 flex flex-1 flex-col md:mt-7">
                  <Link to="/products/$handle" params={{ handle: s.handle }} className="block">
                    <h3 className="font-display text-xl font-bold sku-text transition-opacity hover:opacity-80 sm:text-2xl md:text-[1.7rem]">
                      {s.title}
                    </h3>
                  </Link>
                  <p className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-deep/60 sm:text-[13px] sm:tracking-[0.16em]">
                    {s.tagline}
                  </p>
                  <p className="mt-2.5 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground sm:mt-4 sm:line-clamp-none sm:text-sm">
                    {s.copy}
                  </p>
                  <Link
                    to="/products/$handle"
                    params={{ handle: s.handle }}
                    className="mt-1 inline-flex items-center gap-1 self-start text-[12px] font-semibold text-brand-deep underline-offset-4 hover:underline sm:hidden"
                  >
                    Read more
                    <ArrowRight className="h-3 w-3" />
                  </Link>


                  {/* Price + CTA */}
                  <div className="mt-4 flex items-end justify-between border-t border-black/5 pt-3.5 sm:mt-7 sm:pt-5">
                    {price !== null ? (
                      <SkuPrice price={price} compare={compare} perDay={perDay} />
                    ) : (
                      <span />
                    )}
                  </div>

                  {isComingSoon(s.handle) ? (
                    <p className="mt-2 text-[11.5px] font-semibold text-brand">Coming soon</p>
                  ) : PREORDER_ACTIVE ? (
                    <p className="mt-2 text-[11.5px] font-semibold text-brand">
                      Pre-book · {PREORDER_NOTE_SHORT}
                    </p>
                  ) : null}

                  <div className="mt-3.5 grid grid-cols-[1.1fr_1fr] gap-2 sm:mt-5 sm:grid-cols-2 sm:gap-2.5">
                    <button
                      type="button"
                      onClick={() => setQuickBuyHandle(s.handle)}
                      disabled={SOLD_OUT || isComingSoon(s.handle)}
                      className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand-deep px-3 text-[12.5px] font-semibold tracking-normal text-white transition-all hover:-translate-y-0.5 hover:bg-brand-deep/90 disabled:cursor-not-allowed disabled:opacity-50 sm:h-12 sm:px-5 sm:text-[12px] sm:font-bold sm:uppercase sm:tracking-[0.18em]"
                    >
                      {isComingSoon(s.handle) ? "Coming soon" : t("home.sku.buyNow")}
                    </button>
                    <Link
                      to="/products/$handle"
                      params={{ handle: s.handle }}
                      className="group/cta inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-brand-deep/25 px-3 text-[12.5px] font-semibold tracking-normal text-brand-deep transition-all hover:-translate-y-0.5 hover:bg-brand-soft/50 sm:h-12 sm:gap-2 sm:px-5 sm:text-[12px] sm:font-bold sm:uppercase sm:tracking-[0.18em]"
                    >
                      <span className="sm:hidden">Explore</span>
                      <span className="hidden sm:inline">{t("home.sku.explore")}</span>
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/cta:translate-x-0.5" />
                    </Link>
                  </div>

                </div>
              </FloatingProduct>
            </Reveal>
          );
        })}
      </div>
      <QuickBuySheet
        handle={quickBuyHandle}
        initialProduct={quickBuyProduct}
        onClose={() => setQuickBuyHandle(null)}
      />
    </section>
  );
}


function CertificationStrip() {
  const t = useT();
  const items = [
    { label: t("home.cert.gmp"), icon: BadgeCheck },
    { label: t("home.cert.fssai"), icon: ShieldCheck },
    { label: t("home.cert.usfda"), icon: ShieldCheck },
    { label: t("home.cert.lab"), icon: Microscope },
    { label: t("home.cert.science"), icon: FlaskConical },
    { label: t("home.cert.clean"), icon: Leaf },
  ];
  return (
    <section className="border-y border-border/60 bg-gradient-to-b from-brand-soft/40 to-background">
      <div className="mx-auto max-w-7xl overflow-hidden px-4 py-5 md:px-8 md:py-6">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:gap-x-14">
          {items.map((it) => (
            <li
              key={it.label}
              className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-deep/80"
            >
              <it.icon className="h-4 w-4 text-brand" strokeWidth={1.75} />
              {it.label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function QuizTeaser() {
  const t = useT();
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8">
      <div className="overflow-hidden rounded-[2rem] bg-brand-deep px-6 py-14 text-white md:px-14 md:py-20">
        <div className="grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold md:text-4xl">
              {t("home.quiz.title1")}<br />{t("home.quiz.title2")}
            </h2>
            <p className="mt-4 max-w-md text-white/80">
              {t("home.quiz.body")}
            </p>
            <Link to="/quiz">
              <Button size="lg" className="mt-7 rounded-full bg-white px-7 text-base font-semibold text-brand-deep hover:bg-white/90">
                {t("home.quiz.cta")} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {([
              t("home.quiz.chip.sleep"),
              t("home.quiz.chip.stress"),
              t("home.quiz.chip.hormones"),
              t("home.quiz.chip.gut"),
              t("home.quiz.chip.energy"),
              t("home.quiz.chip.recovery"),
            ]).map(
              (t) => (
                <div
                  key={t}
                  className="rounded-2xl bg-white/10 px-4 py-3 font-medium text-white ring-1 ring-white/15 backdrop-blur"
                >
                  {t}
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyTruWe() {
  const t = useT();
  const reasons = [
    { k: t("home.why.k1"), v: t("home.why.v1") },
    { k: t("home.why.k2"), v: t("home.why.v2") },
    { k: t("home.why.k3"), v: t("home.why.v3") },
    { k: t("home.why.k4"), v: t("home.why.v4") },
  ];
  return (
    <section className="section-y mx-auto max-w-7xl px-4 md:px-8">
      <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:gap-20">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">
            {t("home.why.eyebrow")}
          </p>
          <Reveal>
            <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] text-brand-deep md:text-5xl">
              {t("home.why.title1")}<br />{t("home.why.title2")}
            </h2>
          </Reveal>
          <div className="mt-7 space-y-5 text-base leading-relaxed text-foreground/75 md:text-lg">
            <p>
              {t("home.why.body1")}
            </p>
            <p className="font-medium text-brand-deep">
              {t("home.why.body2")}
            </p>
          </div>
        </div>
        <ul className="grid gap-px overflow-hidden rounded-3xl border border-border bg-border md:self-center">
          {reasons.map((r) => (
            <li key={r.k} className="bg-background px-6 py-6 md:px-8 md:py-7">
              <p className="font-display text-lg font-semibold text-brand-deep md:text-xl">{r.k}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground/70 md:text-base">{r.v}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
